/**
 * HowToVideo - a short walkthrough clip on a /how-to topic.
 *
 * Atomic Design role: Molecule. Each video exists twice under
 * public/videos/how-to/ (see HOW_TO_VIDEO_DIR): a landscape recording of the
 * full desktop app, and a 540×960 portrait recording of the phone layout. This
 * picks the one that matches the screen it's shown on — a phone sees the phone
 * app it's holding, a tablet or computer sees the desktop app — so the buttons
 * in the video sit where the viewer will find them.
 *
 * Built for slow connections in the field:
 *   - preload="none" — nothing downloads until someone presses play; the .jpg
 *     poster (a few KB) stands in until then.
 *   - The clips are silent and captioned on screen, so they work with the
 *     sound off and need no audio track.
 *   - playsInline keeps iPhone from forcing full screen.
 *
 * @example
 * <HowToVideo video="run-inspection" title="Do an inspection" />
 */
import React from 'react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { HOW_TO_VIDEO_DIR } from '../../../content/howToGuide';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface HowToVideoProps {
	/** File name (no extension) under public/videos/how-to/{desktop,mobile}/. */
	video: string;
	/** The topic's title — names the video for screen readers. */
	title: string;
	/** Optional extra classes on the root. */
	className?: string;
}

/** Phones get the portrait clip; `tablet:` (768px) and up get the desktop one. */
const TABLET_QUERY = '(min-width: 768px)';

/** Public URL of one of a video's files. */
export const howToVideoUrl = (video: string, variant: 'desktop' | 'mobile', ext: 'mp4' | 'jpg'): string =>
	`${process.env.PUBLIC_URL ?? ''}/${HOW_TO_VIDEO_DIR}/${variant}/${video}.${ext}`;

/* ============================================
 * COMPONENT
 * ============================================ */

export const HowToVideo: React.FC<HowToVideoProps> = ({ video, title, className = '' }) => {
	const variant = useMediaQuery(TABLET_QUERY) ? 'desktop' : 'mobile';
	const isPortrait = variant === 'mobile';

	return (
		<figure className={`how-to-video how-to-video--${variant} mb-16px ${className}`.trim()} data-ref="molecule-how-to-video">
			{/* key: swap the element when the variant changes (rotating a tablet
			    across the breakpoint), so the new source actually loads. */}
			<video
				key={variant}
				className={[
					'how-to-video__player block rounded-md border border-brand-border bg-gray-100',
					/* Portrait clips would fill a phone screen top to bottom; cap
					   the height so the steps below stay in reach. */
					isPortrait ? 'mx-auto w-auto max-w-full max-h-[70vh] aspect-[9/16]' : 'w-full max-w-[720px] aspect-[720/466]',
				].join(' ')}
				src={howToVideoUrl(video, variant, 'mp4')}
				poster={howToVideoUrl(video, variant, 'jpg')}
				controls
				playsInline
				muted
				preload="none"
				aria-label={`Video: ${title}`}
			>
				{/* Shown only by browsers that can't play video at all. */}
				<a href={howToVideoUrl(video, variant, 'mp4')}>Download the video: {title}</a>
			</video>
			<figcaption className="how-to-video__caption mt-6px text-13px font-sans text-brand-text-muted">
				Watch it done, then follow the steps below. No sound needed — the steps are on screen.
			</figcaption>
		</figure>
	);
};

export default HowToVideo;
